import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import dayjs from "dayjs";
import { getName } from "country-list";

const useStyles = makeStyles({
  card: {
    marginBottom: 12,
    border: "1px solid #e0e0e0",
    boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.14)",
  },
  cardContent: {
    paddingBottom: 8,
  },
  header: {
    marginBottom: 12,
  },
  userName: {
    fontWeight: 600,
    fontSize: 16,
    color: "#006EB3",
  },
  infoGrid: {
    marginTop: 8,
  },
  label: {
    fontSize: 12,
    color: "#666",
    marginBottom: 4,
  },
  value: {
    fontWeight: 500,
    fontSize: 14,
  },
  cardActions: {
    padding: "8px 16px",
    paddingTop: 0,
    gap: 8,
    flexWrap: "wrap",
  },
});

interface UserShape {
  first_name?: string;
  last_name?: string;
  country?: string;
  start_date?: string;
  banned?: boolean;
  username?: string;
  [key: string]: React.ReactNode | boolean | undefined;
}

interface UserCardProps {
  user: UserShape;
  onViewDetails?: (user: UserShape) => void;
  onBan?: (action: string, username?: string) => void;
}

export default function UserCard({ user, onViewDetails, onBan }: UserCardProps): React.ReactElement {
  const classes = useStyles();

  const fullName =
    user.first_name || user.last_name
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : "Not Specified";

  const country = user.country?.length ? getName(user.country) : "Not Specified";
  const startDate = dayjs(user.start_date).format("DD/MM/YYYY");

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <Typography className={classes.userName}>{fullName}</Typography>
        </div>

        <Grid container spacing={2} className={classes.infoGrid}>
          <Grid item xs={12}>
            <Typography className={classes.label}>Email</Typography>
            <Typography className={classes.value}>{user.username}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>Country</Typography>
            <Typography className={classes.value}>{country}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>Start Date</Typography>
            <Typography className={classes.value}>{startDate}</Typography>
          </Grid>
        </Grid>
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={() => onViewDetails!(user)}
        >
          View Details
        </Button>
        <Button
          variant="outlined"
          color={user.banned ? "primary" : "secondary"}
          fullWidth
          onClick={() => onBan!(user.banned ? "unban" : "ban", user.username)}
        >
          {user.banned ? "Unban" : "Ban"}
        </Button>
      </CardActions>
    </Card>
  );
}
